import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Flex } from 'Components/Base';
import ButtonBase from 'Components/Base/Button/ButtonBase';
import { get } from 'Components/Base/constants';
import { Modal } from 'Components/Modal';

import NoInternetConnectionContent from './ModalContent/NoInternetConnectionContent';
import LoadingContent from './ModalContent/LoadingContent';

const CARD_MAX_WIDTH = 343;
/** `radius-2xl` in the eggspace design system. */
const CARD_RADIUS = 16;
const CARD_PADDING = 24;
/** The connecting state has a fixed height in the design; the offline one grows. */
const CONNECTING_CARD_HEIGHT = 382;
/** `radius-lg`, matching TungKai's `size: m` button. */
const BUTTON_RADIUS = 10;
/** `--Stroke-default` in TungKai's `border-white-linear`. */
const BUTTON_INNER_BORDER = 2;
const PRESS_OFFSET = 4;

/**
 * Primary button, size `m`, ported from TungKai's `Button` component. The drop
 * shadow acts as the button's bottom edge and collapses on press, so the button
 * appears to be pushed down.
 */
const RetryButton = styled(ButtonBase)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: 0;
  /* radius-lg */
  border-radius: ${BUTTON_RADIUS}px;

  /*
   * TungKai's \`border-white-linear\`: a 2px inset ring filled with a vertical
   * white gradient. The double mask keeps only the ring and punches out the
   * middle, which is what gives the button its glossy inner edge.
   */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: ${BUTTON_INNER_BORDER}px;
    border-radius: inherit;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 50%,
      rgba(255, 255, 255, 0.2) 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
  background-color: ${get('colors.eggSpace.primary.500')};
  color: ${get('colors.white')};
  font-family: 'IBM Plex Sans Thai Looped', Anuphan, sans-serif;
  ${({ theme }) => theme.textStyles.body2};
  /* TungKai's button label is bolder than the token's weight. */
  font-weight: ${get('fontWeights.bold')};
  box-shadow: 0 ${PRESS_OFFSET}px 0 0 ${get('colors.eggSpace.primary.700')};
  transition: all ${get('duration.fast')};

  @media (hover: hover) {
    &:hover {
      background-color: ${get('colors.eggSpace.primary.300')};
    }
  }

  &:active {
    box-shadow: none;
    transform: translateY(${PRESS_OFFSET}px);
  }

  &:disabled {
    pointer-events: none;
    background-color: ${get('colors.eggSpace.gray.100')};
    color: ${get('colors.eggSpace.gray.500')};
    box-shadow: none;
  }
`;

const ContentWrapper = (props) => {
  return (
    <Flex
      margin="auto"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      {...props}
    />
  );
};

const NoInternetConnectionModal = ({ isOpen, isLoading, onClose, props }) => {
  return (
    <Modal
      disableTopOffset
      contentProps={{ width: '100%' }}
      footerProps={{ mt: 7, width: '100%' }}
      muiProps={{
        isActive: isOpen,
        modalPaperStyle: {
          width: '100%',
          maxWidth: `${CARD_MAX_WIDTH}px`,
          margin: '16px',
          borderRadius: `${CARD_RADIUS}px`,
        },
      }}
      contentWrapperProps={{
        padding: `${CARD_PADDING}px !important`,
        height: isLoading ? `${CONNECTING_CARD_HEIGHT}px` : 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      contents={
        <ContentWrapper>
          {isLoading ? <LoadingContent /> : <NoInternetConnectionContent />}
        </ContentWrapper>
      }
      footer={
        !isLoading && (
          <RetryButton onClick={onClose} data-testid={'btn-retry-connection'}>
            {'เชื่อมต่อใหม่อีกครั้ง'}
          </RetryButton>
        )
      }
      {...props}
    />
  );
};

NoInternetConnectionModal.propTypes = {
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  props: PropTypes.object,
};

export default NoInternetConnectionModal;
